import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Mail, Phone, MapPin, User, Building2,
    Pencil, Send, UserCheck, Trash2, ExternalLink, FileText, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import { cn } from '../lib/cn';
import { useStore } from '../store/useStore';
import { leadService } from '../lib/leadService';
import {
    Lead, LeadStage, LEAD_STAGES, LEAD_STAGE_LABELS, LEAD_SOURCE_LABELS, LEAD_SOURCE_ICONS,
    LEAD_LOST_REASON_LABELS,
} from '../types/leads.types';
import { LeadTimeline } from '../components/leads/LeadTimeline';
import { LeadFormDialog } from '../components/leads/LeadFormDialog';
import { SendLeadEmailModal } from '../components/leads/SendLeadEmailModal';
import { LeadQuickActions } from '../components/leads/LeadQuickActions';
import { LeadNextAction } from '../components/leads/LeadNextAction';
import { LostReasonDialog } from '../components/leads/LostReasonDialog';

export default function LeadDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addNotification } = useStore();
    const [lead, setLead] = useState<Lead | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isEmailOpen, setIsEmailOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmConvert, setConfirmConvert] = useState(false);
    const [converting, setConverting] = useState(false);
    const [generatingOffer, setGeneratingOffer] = useState(false);
    const [lostOpen, setLostOpen] = useState(false);
    const [lostBusy, setLostBusy] = useState(false);

    const load = async () => {
        if (!id) return;
        setLoading(true);
        try {
            setLead(await leadService.getLead(id));
        } catch (error) {
            console.error('Error loading lead:', error);
            navigate('/leads');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    const applyStage = async (
        stage: LeadStage,
        patch: Partial<Pick<Lead, 'lost_reason' | 'next_action' | 'next_action_at'>> = {}
    ) => {
        if (!lead) return;
        const prev = lead.stage;
        setLead({ ...lead, stage, ...patch });
        try {
            await leadService.updateStage(lead.id, stage, prev, patch);
        } catch (error) {
            console.error(error);
            setLead({ ...lead, stage: prev });
            addNotification({ type: 'error', title: 'Errore', message: 'Impossibile aggiornare lo stato' });
        }
    };

    const handleStageChange = async (stage: LeadStage) => {
        if (!lead) return;
        if (stage === 'perso') { setLostOpen(true); return; }
        await applyStage(stage, lead.stage === 'perso' ? { lost_reason: null } : {});
    };

    const confirmLost = async (reason: string) => {
        setLostBusy(true);
        await applyStage('perso', { lost_reason: reason });
        setLostBusy(false);
        setLostOpen(false);
    };

    const handleDelete = async () => {
        if (!lead) return;
        setIsDeleting(true);
        try {
            await leadService.deleteLead(lead.id);
            addNotification({ type: 'success', title: 'Lead eliminato' });
            navigate('/leads');
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore eliminazione' });
        } finally {
            setIsDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleConvert = async () => {
        if (!lead) return;
        setConverting(true);
        try {
            const customerId = await leadService.convertToCustomer(lead);
            addNotification({ type: 'success', title: 'Lead convertito in cliente' });
            navigate(`/customers/${customerId}`);
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore conversione', message: 'Impossibile convertire il lead' });
        } finally {
            setConverting(false);
            setConfirmConvert(false);
        }
    };

    const handleGenerateOffer = async () => {
        if (!lead) return;
        setGeneratingOffer(true);
        try {
            const { priceListId } = await leadService.createOfferFromLead(lead);
            addNotification({
                type: 'success',
                title: 'Offerta pronta',
                message: 'Cliente prospect creato. Puoi inserire i prodotti.',
            });
            navigate(`/price-lists?edit=${priceListId}`);
        } catch (error) {
            console.error(error);
            addNotification({
                type: 'error',
                title: 'Errore',
                message: 'Impossibile generare l\'offerta dal lead',
            });
        } finally {
            setGeneratingOffer(false);
        }
    };

    if (loading) return <div className="p-8">Caricamento...</div>;
    if (!lead) return <div className="p-8">Lead non trovato</div>;

    const SourceIcon = LEAD_SOURCE_ICONS[lead.source] || User;
    const title = lead.full_name || lead.company_name || 'Lead senza nome';
    const stageMeta = LEAD_STAGES.find((s) => s.key === lead.stage);

    return (
        <div className="mx-auto w-full max-w-7xl py-2 md:py-6 animate-in fade-in duration-500">
            <ConfirmDeleteDialog
                open={confirmDelete}
                onOpenChange={(open) => !open && setConfirmDelete(false)}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
                title="Elimina Lead"
                description={`Sei sicuro di voler eliminare "${title}"? L'azione è irreversibile.`}
            />
            <ConfirmDeleteDialog
                open={confirmConvert}
                onOpenChange={(open) => !open && setConfirmConvert(false)}
                onConfirm={handleConvert}
                isDeleting={converting}
                title="Converti in Cliente"
                description="Verrà creato un nuovo cliente con i dati di questo lead, che passerà allo stato 'Vinto'. Continuare?"
                confirmLabel="Converti"
                busyLabel="Conversione..."
                confirmClassName="bg-green-600 hover:bg-green-700"
            />

            <Button variant="ghost" className="mb-4 pl-0 hover:pl-2 transition-all" onClick={() => navigate('/leads')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Torna ai lead
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
                {/* Colonna sinistra: info + azioni */}
                <div className="space-y-4 md:space-y-6">
                    <Card className="p-4 md:p-6 border-t-4 border-t-blue-600 shadow-lg">
                        <div className="flex items-center gap-2 mb-1">
                            <SourceIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500">{LEAD_SOURCE_LABELS[lead.source]}</span>
                        </div>
                        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 break-words">{title}</h1>

                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', stageMeta?.badgeClass)}>
                                {LEAD_STAGE_LABELS[lead.stage]}
                            </span>
                            {lead.stage === 'perso' && lead.lost_reason && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                                    {LEAD_LOST_REASON_LABELS[lead.lost_reason.split(':')[0]] || lead.lost_reason}
                                </span>
                            )}
                            {lead.converted_customer_id && (
                                <button
                                    onClick={() => navigate(`/customers/${lead.converted_customer_id}`)}
                                    className={cn(
                                        'flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border',
                                        lead.stage === 'vinto'
                                            ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                                            : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                                    )}
                                >
                                    <ExternalLink className="w-3 h-3" />
                                    {lead.stage === 'vinto' ? 'Cliente collegato' : 'Prospect collegato'}
                                </button>
                            )}
                        </div>

                        <div className="space-y-3 text-sm">
                            {lead.full_name && lead.company_name && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <Building2 className="w-4 h-4 text-gray-400" />
                                    <span>{lead.company_name}</span>
                                </div>
                            )}
                            {lead.email && (
                                <div className="flex items-center gap-3 text-gray-700 min-w-0">
                                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <a href={`mailto:${lead.email}`} className="hover:underline text-blue-600 break-all">{lead.email}</a>
                                </div>
                            )}
                            {lead.phone && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a>
                                </div>
                            )}
                            {(lead.city || lead.province) && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span>{[lead.city, lead.province].filter(Boolean).join(', ')}</span>
                                </div>
                            )}
                            {lead.assignee && (
                                <div className="flex items-center gap-3 text-gray-700 pt-2 border-t">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs text-gray-500">
                                        Assegnato a {lead.assignee.full_name || lead.assignee.email}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Registra contatto in 1 click (azzera il contatore "giorni fermo") */}
                        <div className="mt-4 pt-4 border-t">
                            <div className="text-xs text-gray-400 mb-2">Registra contatto</div>
                            <LeadQuickActions
                                lead={lead}
                                onLogged={load}
                                onEmail={() => setIsEmailOpen(true)}
                            />
                        </div>

                        {(lead.campaign_name || lead.form_name) && (
                            <div className="mt-4 pt-4 border-t text-xs text-gray-500 space-y-1">
                                {lead.campaign_name && <div>Campagna: {lead.campaign_name}</div>}
                                {lead.form_name && <div>Form: {lead.form_name}</div>}
                            </div>
                        )}

                        {lead.notes && (
                            <div className="mt-4 pt-4 border-t">
                                <div className="text-xs text-gray-400">Note</div>
                                <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{lead.notes}</div>
                            </div>
                        )}

                        {/* Prossima azione */}
                        <LeadNextAction lead={lead} onSaved={load} />

                        {/* Stato pipeline */}
                        <div className="mt-6 pt-4 border-t">
                            <label className="text-xs text-gray-400 block mb-1">Stato pipeline</label>
                            <Select value={lead.stage} onValueChange={(v) => handleStageChange(v as LeadStage)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {LEAD_STAGES.map((s) => (
                                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Azioni */}
                        <div className="mt-4 grid grid-cols-2 gap-2">
                            {(lead.stage === 'qualificato' || lead.stage === 'proposta' || lead.converted_customer_id) && lead.stage !== 'perso' && (
                                <Button
                                    className="col-span-2 bg-amber-600 hover:bg-amber-700 text-white"
                                    onClick={handleGenerateOffer}
                                    disabled={generatingOffer}
                                >
                                    {generatingOffer ? (
                                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                        <FileText className="w-4 h-4 mr-1" />
                                    )}
                                    {generatingOffer ? 'Generazione...' : 'Genera Offerta'}
                                </Button>
                            )}
                            <Button variant="outline" onClick={() => setIsEditOpen(true)}>
                                <Pencil className="w-4 h-4 mr-1" /> Modifica
                            </Button>
                            <Button variant="outline" onClick={() => setIsEmailOpen(true)} disabled={!lead.email}>
                                <Send className="w-4 h-4 mr-1" /> Email
                            </Button>
                            {lead.converted_customer_id && (
                                <Button variant="outline" className="col-span-2" onClick={() => navigate(`/customers/${lead.converted_customer_id}`)}>
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    {lead.stage === 'vinto' ? 'Apri Cliente' : 'Apri Prospect'}
                                </Button>
                            )}
                            {lead.stage !== 'vinto' && lead.stage !== 'perso' && (
                                <Button variant="outline" className="col-span-2 text-green-700 hover:text-green-800" onClick={() => setConfirmConvert(true)}>
                                    <UserCheck className="w-4 h-4 mr-1" /> Converti in Cliente
                                </Button>
                            )}
                            <Button variant="ghost" className="col-span-2 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setConfirmDelete(true)}>
                                <Trash2 className="w-4 h-4 mr-1" /> Elimina Lead
                            </Button>
                        </div>
                    </Card>
                </div>

                {/* Colonna destra: timeline */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6">
                    <div className="bg-white rounded-lg p-3 md:p-6 shadow-sm border">
                        <LeadTimeline leadId={lead.id} />
                    </div>
                </div>
            </div>

            <LeadFormDialog
                open={isEditOpen}
                onOpenChange={setIsEditOpen}
                lead={lead}
                onSaved={load}
            />
            <SendLeadEmailModal
                isOpen={isEmailOpen}
                onClose={() => setIsEmailOpen(false)}
                lead={lead}
                onSent={load}
            />
            <LostReasonDialog
                open={lostOpen}
                leadName={lead.full_name || lead.company_name || undefined}
                busy={lostBusy}
                onCancel={() => setLostOpen(false)}
                onConfirm={confirmLost}
            />
        </div>
    );
}
